<script setup lang="ts">
  import { BottomSheet, Typo, TypoVariant } from '@travelata-libs/ui-kit';
  import { computed, ref } from 'vue';
  import type { DestinationPrices } from '@/entities/searchCriteria';
  import type { SearchFormGoal, SearchFormTracking } from '@/entities/searchForm';
  import type { FlipDirection } from '@/shared/composables';
  import FlipView from '@/shared/ui/FlipView.vue';
  import MobilePrimaryButton from '@/shared/ui/MobilePrimaryButton.vue';
  import MobileSearchFields from './MobileSearchFields.vue';
  import MobileCalendarScreen from './screens/MobileCalendarScreen.vue';
  import MobileDepartureScreen from './screens/MobileDepartureScreen.vue';
  import MobileDestinationScreen from './screens/MobileDestinationScreen.vue';
  import MobileNightsScreen from './screens/MobileNightsScreen.vue';
  import MobileTouristsScreen from './screens/MobileTouristsScreen.vue';
  import { SEARCH_FORM_MOBILE_TEXTS } from '../../config/texts';
  import { type SearchFormCriteria } from '../../model/types';
  import { provideMobileSearchFlow, useMobileSearchFlow, type MobileScreen } from '../../model/useMobileSearchFlow';
  import { useSearchFormGoals } from '../../model/useSearchFormGoals';

  type SheetScreen = Extract<MobileScreen, 'departure' | 'destination'>;

  interface SearchFormBottomSheetProps {
    modelValue: SearchFormCriteria;
    formUrl?: string;
    prices?: DestinationPrices;
    isVisibleDeparture?: boolean;
    isVisibleRoomButton?: boolean;
    title?: string;
    searchBtnText?: string;
    dateFieldLabel?: string;
    nightsFieldLabel?: string;
    tracking?: SearchFormTracking;
  }

  const props = withDefaults(defineProps<SearchFormBottomSheetProps>(), {
    formUrl: '',
    prices: undefined,
    isVisibleDeparture: true,
    isVisibleRoomButton: true,
    title: SEARCH_FORM_MOBILE_TEXTS.editTitle,
    searchBtnText: undefined,
    dateFieldLabel: undefined,
    nightsFieldLabel: undefined,
    tracking: undefined,
  });

  interface SearchFormBottomSheetEmits {
    (e: 'update:modelValue', value: SearchFormCriteria): void;
    (e: 'search', value: SearchFormCriteria): void;
    (e: 'close'): void;
    (e: 'track', goal: SearchFormGoal): void;
  }

  const emit = defineEmits<SearchFormBottomSheetEmits>();
  const isLoading = computed(() => !props.modelValue.destination);
  const goals = useSearchFormGoals(props, (goal) => emit('track', goal), isLoading);

  const modelValue = computed<SearchFormCriteria>(() => props.modelValue);
  const prices = computed<DestinationPrices | undefined>(() => props.prices);
  const isVisibleDeparture = computed<boolean>(() => props.isVisibleDeparture);
  const isVisibleRoomButton = computed<boolean>(() => props.isVisibleRoomButton);
  const searchBtnText = computed<string | undefined>(() => props.searchBtnText);
  const dateFieldLabel = computed<string | undefined>(() => props.dateFieldLabel);
  const nightsFieldLabel = computed<string | undefined>(() => props.nightsFieldLabel);

  const bottomSheetRef = ref<null | { close: () => Promise<void> }>(null);
  const activeScreen = ref<SheetScreen | null>(null);

  const isSheetScreen = (screen: MobileScreen): screen is SheetScreen =>
    screen === 'departure' || screen === 'destination';

  const screenDirection = computed<FlipDirection>(() => (activeScreen.value ? 'forward' : 'back'));

  const flow = useMobileSearchFlow({
    modelValue,
    formUrl: () => props.formUrl,
    prices,
    isVisibleDeparture,
    isVisibleRoomButton,
    embedded: true,
    nav: {
      open: (screen: MobileScreen) => {
        if (isSheetScreen(screen)) {
          activeScreen.value = screen;
        }
      },
      back: () => {
        activeScreen.value = null;
      },
    },
    onUpdate: (value: SearchFormCriteria) => emit('update:modelValue', value),
    onSearch: () => {
      goals.trackSearchClick();
      emit('search', props.modelValue);
    },
    searchBtnText,
    dateFieldLabel,
    nightsFieldLabel,
  });
  provideMobileSearchFlow(flow);

  const onSubmit = async (): Promise<void> => {
    flow.submit();
    await bottomSheetRef.value?.close();
  };
</script>

<script lang="ts">
  export default {
    model: {
      prop: 'modelValue',
      event: 'update:modelValue',
    },
  };
</script>

<template>
  <div
    class="search-form-bottom-sheet"
    data-testid="search-form-bottom-sheet"
  >
    <BottomSheet
      ref="bottomSheetRef"
      root-class="search-form-bottom-sheet__sheet"
      :animated="true"
      :icon-closable="true"
      @close="emit('close')"
    >
      <template #header>
        <Typo
          class="search-form-bottom-sheet__title"
          :variant="TypoVariant.Heading5"
          tag="span"
          data-testid="search-form-bottom-sheet-title"
        >
          {{ title }}
        </Typo>
      </template>

      <MobileSearchFields filled />

      <template #footer>
        <MobilePrimaryButton
          testid="search-form-bottom-sheet-primary"
          @click="onSubmit"
        >
          {{ flow.primaryTexts.searchBtn }}
        </MobilePrimaryButton>
      </template>
    </BottomSheet>
    <FlipView :direction="screenDirection">
      <MobileDepartureScreen
        v-if="activeScreen === 'departure'"
        @touchmove.native.stop
      />
      <MobileDestinationScreen
        v-else-if="activeScreen === 'destination'"
        @touchmove.native.stop
      />
    </FlipView>
    <MobileCalendarScreen v-if="flow.isCalendarOpen.value" />
    <MobileNightsScreen v-if="flow.isNightsOpen.value" />
    <MobileTouristsScreen v-if="flow.isTouristsOpen.value" />
  </div>
</template>

<style scoped lang="scss">
  .search-form-bottom-sheet {
    &__title {
      padding-top: var(--spacing-xxs);
      text-align: center;
      width: 100%;
    }
  }
</style>
